import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { reduxForm, initialize, Field, getFormValues } from 'redux-form';
import { Input, TextArea } from 'components/Input';
import { renderCheckInput } from 'utils/renderCheckInput';
import { isRequired, isCommaSeparatedEmailList, isAtLeastOneChecked, splitStrings } from 'utils/formValidators';
import { get, size } from 'lodash';
import { websiteSelector, isConsumerAdminSelector } from 'selectors';
import { Form } from 'components/Form';
import { api } from 'utils/api';
import { changeWebsite } from 'actions';
import { CheckboxGroup } from 'components/CheckboxGroup';

const formName = 'CommentsFeature';

export function CommentsFeature({ formValues, isConsumerAdmin, ...props }) {
  const { t } = useTranslation();

  const isCommentsFeatureEnabled = get(formValues, 'enabled', false);
  const isEmailFieldSet = Boolean(get(formValues, 'emails'));
  const isLanguagesFieldSet = Boolean(size(get(formValues, 'languages')));

  const toggleTitle = isCommentsFeatureEnabled
    ? t('features.comments.comments-toggle.enabled')
    : t('features.comments.comments-toggle.disabled');

  return (
    <Form {...props} formTitle="features.comments.title">
      {!isConsumerAdmin && <Field name="enabled" type="switch" title={toggleTitle} component={renderCheckInput} />}

      <Field
        name="hint"
        title={t('features.comments.hint.label')}
        description={t('features.comments.hint.description')}
        component={TextArea}
      />

      <Field
        name="emails"
        title={t('features.comments.emails.label')}
        description={t('features.comments.emails.description')}
        component={Input}
        isMandatory={isLanguagesFieldSet}
        validate={isLanguagesFieldSet ? [isRequired, isCommaSeparatedEmailList] : isCommaSeparatedEmailList}
      />

      <Field
        name="languages"
        title={t('features.comments.languages.label')}
        component={CheckboxGroup}
        isMandatory={isEmailFieldSet}
        validate={isEmailFieldSet ? isAtLeastOneChecked : null}
        options={supportedLanguages}
        translateOption={(opt) => t(`features.comments.languages.${opt}`)}
      />
    </Form>
  );
}

const updateWebsite =
  (id) =>
  (dispatch) =>
  ({ enabled, hint, emails, languages }) => {
    const commentFeature = {
      enabled,
      hint,
      notification: {
        emails: splitStrings(emails),
        languages,
      },
    };

    return api.patch(`/websites/${id}`, { commentFeature }).then((payload) => {
      dispatch(initialize(formName, getInitialValues(payload.commentFeature)));
      dispatch(changeWebsite(payload));
    });
  };

const supportedLanguages = ['en', 'de'];
const joinEmails = (emails = []) => emails.join(', ');

const getInitialValues = (reportVideoFeature) => {
  const enabled = get(reportVideoFeature, 'enabled', false);
  const hint = get(reportVideoFeature, 'hint');
  const notification = get(reportVideoFeature, 'notification', {});

  return {
    enabled,
    hint,
    emails: joinEmails(notification.emails),
    languages: notification.languages,
  };
};

export const CommentsFeatureDecorated = compose(
  connect(
    (state) => ({
      website: websiteSelector(state),
      formValues: getFormValues(formName)(state),
      isConsumerAdmin: isConsumerAdminSelector(state),
      formName,
    }),
    {
      updateWebsite,
    },
    (stateProps, dispatchProps) => {
      const commentFeature = get(stateProps.website, 'commentFeature');

      const { formValues, isConsumerAdmin } = stateProps;
      const initialValues = getInitialValues(commentFeature);

      return {
        formName,
        isConsumerAdmin,
        formValues,
        initialValues,
        onSubmit: dispatchProps.updateWebsite(get(stateProps.website, 'id')),
      };
    }
  ),
  reduxForm({
    form: formName,
    touchOnChange: true,
    enableReinitialize: true,
  })
)(CommentsFeature);
